import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import DateFilter, {
  DateFields,
} from '../../../../components/common/table/date-filter/date-filter';
import { DefaultFilter } from '../../../../components/common/table/default-filter';
import Table from '../../../../components/common/table/table';
import { ButtonComponent } from '../../../../components/form/elements/button';
import FieldTypes from '../../../../enums/field-types';
import Work from '../../../../interfaces/work';
import {
  CROP_FIELDS_AND_WORKS_INDEX,
  CROP_WORK_VIEW,
} from '../../../../router/routes';
import { useService } from '../../../../services/services';
import { can, route } from '../../../../services/utils';
import css from './index.module.css';
import ExcelButton from '../../../../components/common/table/excel-button/excel-button';
import { useQuery } from '../../../../hooks/use-query';
import ShowIfAllowed from '../../../../components/common/show-if-allowed';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { useBackButton } from '../backButtonContext';
import { Section } from '../index';

export default function WorkOverview() {
  const { t } = useTranslation();
  const tp = useCallback(
    (key: string) => t('pages.crop.works.'.concat(key)),
    [t]
  );
  const history = useHistory();
  const [params] = useQuery();
  const [init, setInit] = useState<boolean>(true);
  const dateFilterName = 'work-overview';
  const { workService } = useService();
  const { farmId, season } = useParams<{ farmId: string; season: string }>();
  const selectedWorksState = useState<Work[]>([]);
  const [fieldLadNumberFilter, setFieldLadNumberFilter] = useState([]);
  const [fieldNameFilter, setFieldNameFilter] = useState([]);
  const [employeeNameFilter, setEmployeeNameFilter] = useState([]);
  const [typeNameFilter, setTypeNameFilter] = useState([]);
  const [workValueFilter, setWorkValueFilter] = useState([]);
  const { setBackButton } = useBackButton();

  const { farmUser } = useSelector((state: RootState) => ({
    farmUser: state.global.farmUser,
  }));

  const request = useCallback(
    (page: number, sort: string, desc: boolean, filter: any) =>
      workService.index(farmId, season, { page, sort, desc, ...filter }),
    [workService, farmId, season]
  );

  const setDone = useCallback(() => {
    const selectedWorkIds = selectedWorksState[0]
      .filter((work) => !work.done)
      .map((work) => work.id);

    if (selectedWorkIds.length > 0) {
      workService.setDone(farmId, selectedWorkIds).subscribe(() => {
        const search = new URLSearchParams(history.location.search);
        search.append('loaded', Date.now().toString());

        history.push({ ...history.location, search: search.toString() });
      });
    }
  }, [selectedWorksState, farmId, workService, history]);

  useEffect(() => {
    setBackButton(
      route(CROP_FIELDS_AND_WORKS_INDEX, {
        farmId: farmId,
        season: season,
        tab: Section.workOverview,
      })
    );
  }, [farmId, season, setBackButton]);

  // set filter to current month on page load
  useEffect(() => {
    if (init && !params[DateFields.to]) {
      let fromDate = sessionStorage.getItem(
        dateFilterName.concat('-', DateFields.from)
      );
      let toDate = sessionStorage.getItem(
        dateFilterName.concat('-', DateFields.to)
      );

      const search = new URLSearchParams(history.location.search);

      if (fromDate && fromDate !== 'null') {
        search.set(DateFields.from, fromDate);
      }

      if (toDate && toDate !== 'null') {
        search.set(DateFields.to, toDate);
      }
      history.replace({ ...history.location, search: search.toString() });
    }
    setInit(false);
  }, [init, history, params]);

  useEffect(() => {
    const worksDoneFilter = params.done ? { done: params.done } : {};

    const subscription = workService
      .usedFields(farmId, season, worksDoneFilter)
      .subscribe((fields) => {
        setFieldLadNumberFilter(
          fields.map((field) => ({ label: field.lad_number, value: field.id }))
        );
        setFieldNameFilter(
          fields.map((field) => ({ label: field.name, value: field.id }))
        );
      });
    return () => subscription.unsubscribe();
  }, [farmId, season, workService, params]);

  useEffect(() => {
    const subscription = workService
      .usedEmployees(farmId, season)
      .subscribe((employees) => {
        setEmployeeNameFilter(
          employees.map((employee) => {
            return { label: employee.full_name, value: employee.id };
          })
        );
      });
    return () => subscription.unsubscribe();
  }, [farmId, season, workService]);

  useEffect(() => {
    const subscription = workService
      .usedCropWorkTypes(farmId, season)
      .subscribe((cropWorkTypes) => {
        setTypeNameFilter(
          cropWorkTypes.map((cropWorkType) => {
            return { label: cropWorkType.name, value: cropWorkType.id };
          })
        );
      });
    return () => subscription.unsubscribe();
  }, [farmId, season, workService]);

  useEffect(() => {
    const subscription = workService
      .usedWorks(farmId, season)
      .subscribe((works) => {
        setWorkValueFilter(
          works.map((work) => {
            return { label: work.value, value: work.id };
          })
        );
      });
    return () => subscription.unsubscribe();
  }, [farmId, season, workService]);

  const toolbar = useCallback(() => {
    return (
      <div className={css['toolbar']}>
        <div>
          <ShowIfAllowed ability={{ title: 'update work' }}>
            <ButtonComponent
              name="set_done"
              label={tp('set_done')}
              click={setDone}
            />
          </ShowIfAllowed>
        </div>
        <div>
          <DateFilter
            name={dateFilterName}
            fields={[DateFields.from, DateFields.to]}
          />
          <ExcelButton
            id={'work-overview-excel-btn'}
            onClick={() => {
              return workService
                .exportWorkOverviewXls(farmId, season)
                .subscribe();
            }}
          />
        </div>
      </div>
    );
  }, [tp, setDone, farmId, season, workService]);

  return (
    <div className={css['index']}>
      <Table<Work>
        toolbar={toolbar}
        request={request}
        keyAccessor="id"
        columns={[
          {
            accessor: 'done',
            header: tp('done'),
            filter: (
              <DefaultFilter
                options={[
                  { value: 'true', label: t('yes') },
                  { value: 'false', label: t('no') },
                ]}
                name="done"
              />
            ),
          },
          {
            accessor: 'date',
            header: tp('date'),
            valuable: (value, work: Work) =>
              work.planned ? tp('planned') : value,
            sortable: true,
          },
          {
            accessor: 'type.name',
            header: tp('type'),
            filter: <DefaultFilter options={typeNameFilter} name="type" />,
          },
          {
            accessor: 'work.value',
            header: tp('work'),
            filter: <DefaultFilter options={workValueFilter} name="work" />,
          },
          {
            accessor: 'field_lad_number_agg',
            dataType: FieldTypes.JSON_AGG,
            header: t('pages.crop.fields_and_works.fields.lad_number'),
            filter: (
              <DefaultFilter
                options={fieldLadNumberFilter}
                name="field_lad_number"
              />
            ),
          },
          {
            accessor: 'field_name_agg',
            dataType: FieldTypes.JSON_AGG,
            header: tp('field'),
            filter: (
              <DefaultFilter options={fieldNameFilter} name="field_name" />
            ),
          },
          {
            accessor: 'employee_name_agg',
            dataType: FieldTypes.JSON_AGG,
            header: tp('employee'),
            filter: (
              <DefaultFilter options={employeeNameFilter} name="employee" />
            ),
          },
        ]}
        viewRoute={route(CROP_WORK_VIEW, {
          farmId: farmId,
          season: season,
        })}
        viewRecordAbility={{ title: 'view work' }}
        selectedRowsState={
          can(farmUser, { title: 'update work' }) ? selectedWorksState : null
        }
        rowClassSetter={(row) =>
          row.done || row.planned === true ? css['done-true'] : ''
        }
      />
    </div>
  );
}
